import { getAuthToken } from './auth/tokenStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../config';
import { createLogger } from '../utils/logger';

const logger = createLogger('DeathVerification');

// Enums matching backend
export enum RecipientRelationship {
  ATTORNEY = 'attorney',
  EXECUTOR = 'executor',
  SIBLING = 'sibling',
  PARENT = 'parent',
  CHILD = 'child',
  SPOUSE = 'spouse',
  FRIEND = 'friend',
  ACCOUNTANT = 'accountant',
  FINANCIAL_ADVISOR = 'financial_advisor',
  OTHER = 'other',
}

export enum AccessDelay {
  IMMEDIATE = 0,
  ONE_DAY = 24,
  SEVEN_DAYS = 168,
  FOURTEEN_DAYS = 336,
  THIRTY_DAYS = 720,
  NEVER = -1,
}

// Interfaces
export interface SecretCodeRecipient {
  id: string;
  primaryUserId: string;
  priority: number;
  recipientName: string;
  relationship: RecipientRelationship;
  phoneNumber: string;
  email: string | null;
  isActive: boolean;
  lastVerifiedAt: string | null;
  verificationReminderSentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSecretCodeRecipientDto {
  priority: number;
  recipientName: string;
  relationship: RecipientRelationship;
  phoneNumber: string;
  email?: string;
}

export interface UpdateSecretCodeRecipientDto {
  priority?: number;
  recipientName?: string;
  relationship?: RecipientRelationship;
  phoneNumber?: string;
  email?: string;
  isActive?: boolean;
}

export interface DeathAccessPolicy {
  id: string;
  primaryUserId: string;
  emergencyContactsDelay: number;
  funeralWishesDelay: number;
  medicalInfoDelay: number;
  personalInfoDelay: number;
  financialInfoDelay: number;
  propertyInfoDelay: number;
  documentsDelay: number;
  passwordsDelay: number;
  loyaltyPointsDelay: number;
  contactsDelay: number;
  expensesDelay: number;
  requireSecretCodeValidation: boolean;
  requireExternalVerification: boolean;
  allowLivingConfirmationOverride: boolean;
  livingConfirmationTimeoutHours: number;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateDeathAccessPolicyDto {
  emergencyContactsDelay?: number;
  funeralWishesDelay?: number;
  medicalInfoDelay?: number;
  personalInfoDelay?: number;
  financialInfoDelay?: number;
  propertyInfoDelay?: number;
  documentsDelay?: number;
  passwordsDelay?: number;
  loyaltyPointsDelay?: number;
  contactsDelay?: number;
  expensesDelay?: number;
  requireSecretCodeValidation?: boolean;
  requireExternalVerification?: boolean;
  allowLivingConfirmationOverride?: boolean;
  livingConfirmationTimeoutHours?: number;
}

export interface DelayOption {
  value: number;
  label: string;
}

export interface PolicyResponse {
  policy: DeathAccessPolicy;
  delayOptions: DelayOption[];
  categories: string[];
}

// Relationship display names
export const RELATIONSHIP_LABELS: Record<RecipientRelationship, string> = {
  [RecipientRelationship.ATTORNEY]: 'Attorney',
  [RecipientRelationship.EXECUTOR]: 'Executor',
  [RecipientRelationship.SIBLING]: 'Sibling',
  [RecipientRelationship.PARENT]: 'Parent',
  [RecipientRelationship.CHILD]: 'Child',
  [RecipientRelationship.SPOUSE]: 'Spouse',
  [RecipientRelationship.FRIEND]: 'Friend',
  [RecipientRelationship.ACCOUNTANT]: 'Accountant',
  [RecipientRelationship.FINANCIAL_ADVISOR]: 'Financial Advisor',
  [RecipientRelationship.OTHER]: 'Other',
};

// Delay display labels
export const DELAY_LABELS: Record<number, string> = {
  [AccessDelay.IMMEDIATE]: 'Immediate',
  [AccessDelay.ONE_DAY]: '1 Day',
  [AccessDelay.SEVEN_DAYS]: '7 Days',
  [AccessDelay.FOURTEEN_DAYS]: '14 Days',
  [AccessDelay.THIRTY_DAYS]: '30 Days',
  [AccessDelay.NEVER]: 'Never (Manual Only)',
};

// Category display names
export const CATEGORY_LABELS: Record<string, string> = {
  emergency_contacts: 'Emergency Contacts',
  funeral_wishes: 'Final Wishes',
  medical_info: 'Medical Information',
  personal_info: 'Personal Information',
  financial_info: 'Financial Information',
  property_info: 'Property Information',
  documents: 'Documents',
  passwords: 'Passwords',
  loyalty_points: 'Loyalty Points',
  contacts: 'Contacts',
  expenses: 'Expenses',
};

class DeathVerificationService {
  private async getAuthToken(): Promise<string | null> {
    return await getAuthToken();
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getAuthToken();

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });

    return response;
  }

  // ============================================
  // Secret Code Recipients
  // ============================================

  /**
   * Get all secret code recipients for the current user
   */
  async getRecipients(): Promise<SecretCodeRecipient[]> {
    try {
      const response = await this.makeRequest('/api/death-verification/recipients', {
        method: 'GET',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to get recipients: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error getting secret code recipients:', error);
      throw error;
    }
  }

  /**
   * Create a new secret code recipient
   */
  async createRecipient(data: CreateSecretCodeRecipientDto): Promise<SecretCodeRecipient> {
    try {
      const response = await this.makeRequest('/api/death-verification/recipients', {
        method: 'POST',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create recipient: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error creating secret code recipient:', error);
      throw error;
    }
  }

  /**
   * Update a secret code recipient
   */
  async updateRecipient(id: string, data: UpdateSecretCodeRecipientDto): Promise<SecretCodeRecipient> {
    try {
      const response = await this.makeRequest(`/api/death-verification/recipients/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update recipient: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error updating secret code recipient:', error);
      throw error;
    }
  }

  /**
   * Verify a recipient's contact information is still correct
   */
  async verifyRecipient(id: string): Promise<SecretCodeRecipient> {
    try {
      const response = await this.makeRequest(`/api/death-verification/recipients/${id}/verify`, {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to verify recipient: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error verifying secret code recipient:', error);
      throw error;
    }
  }

  /**
   * Delete a secret code recipient
   */
  async deleteRecipient(id: string): Promise<void> {
    try {
      const response = await this.makeRequest(`/api/death-verification/recipients/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete recipient: ${response.status}`);
      }
    } catch (error) {
      logger.error('Error deleting secret code recipient:', error);
      throw error;
    }
  }

  /**
   * Swap priorities between two recipients
   */
  async swapRecipientPriorities(recipientId1: string, recipientId2: string): Promise<void> {
    try {
      const response = await this.makeRequest('/api/death-verification/recipients/swap-priorities', {
        method: 'POST',
        body: JSON.stringify({ recipientId1, recipientId2 }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to swap priorities: ${response.status}`);
      }
    } catch (error) {
      logger.error('Error swapping recipient priorities:', error);
      throw error;
    }
  }

  // ============================================
  // Death Access Policy
  // ============================================

  /**
   * Get death access policy for the current user
   */
  async getPolicy(): Promise<PolicyResponse> {
    try {
      logger.debug('[DeathVerificationService] Making getPolicy request...');
      const response = await this.makeRequest('/api/death-verification/policy', {
        method: 'GET',
      });

      logger.debug('[DeathVerificationService] Response status:', response.status);
      logger.debug('[DeathVerificationService] Response ok:', response.ok);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.error('[DeathVerificationService] Error response:', errorData);
        throw new Error(errorData.message || `Failed to get policy: ${response.status}`);
      }

      const data = await response.json();
      logger.debug('[DeathVerificationService] Parsed JSON data:', JSON.stringify(data, null, 2));
      return data;
    } catch (error) {
      logger.error('[DeathVerificationService] Error getting death access policy:', error);
      throw error;
    }
  }

  /**
   * Update death access policy
   */
  async updatePolicy(data: UpdateDeathAccessPolicyDto): Promise<DeathAccessPolicy> {
    try {
      logger.debug('[DeathVerificationService] Sending update request with data:', JSON.stringify(data, null, 2));

      const response = await this.makeRequest('/api/death-verification/policy', {
        method: 'PUT',
        body: JSON.stringify(data),
      });

      logger.debug('[DeathVerificationService] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.error('[DeathVerificationService] Error response:', errorData);
        throw new Error(errorData.message || `Failed to update policy: ${response.status}`);
      }

      const result = await response.json();
      logger.debug('[DeathVerificationService] Parsed response:', JSON.stringify(result, null, 2));
      return result;
    } catch (error) {
      logger.error('[DeathVerificationService] Error updating death access policy:', error);
      throw error;
    }
  }

  /**
   * Update a specific category delay
   */
  async updateCategoryDelay(category: string, delayHours: number): Promise<DeathAccessPolicy> {
    try {
      const response = await this.makeRequest(`/api/death-verification/policy/category/${category}`, {
        method: 'PUT',
        body: JSON.stringify({ delayHours }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update category delay: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error updating category delay:', error);
      throw error;
    }
  }

  /**
   * Get available delay options
   */
  async getDelayOptions(): Promise<{
    delayOptions: DelayOption[];
    categories: string[];
    sensitiveCategories: string[];
  }> {
    try {
      const response = await this.makeRequest('/api/death-verification/policy/options', {
        method: 'GET',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to get delay options: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error getting delay options:', error);
      throw error;
    }
  }
}

export const deathVerificationService = new DeathVerificationService();
